/**
 * PixelOffice Canvas 渲染器
 *
 * 参照 index.html 的 Phaser 实现，在原生 Canvas 2D 上复刻相同的
 * 精灵布局、帧率、动画状态与交互行为。
 *
 * 场景配置数据来源：
 *   - preload() → 所有 spritesheet / image 的尺寸与路径
 *   - create()  → 精灵坐标、原点、层级（depth）、初始动画
 *   - update()  → 气泡定时器、状态机帧更新
 *   - applyVisualState() → 按 OfficeStatus 控制可见性与动画
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { BUBBLE_TEXTS, CAT_BUBBLE_TEXTS, type OfficeStatus } from './spriteConfig';

// ─── 资源导入 ──────────────────────────────────────────────────────────────────
import imgOfficeBg from '@src/assets/pixel-office/sl.webp'; // 默认"森林"主题背景（forest）
import imgBgXd from '@src/assets/pixel-office/xd.webp'; // 现代
import imgBgXt from '@src/assets/pixel-office/xt.webp'; // 雪天
import imgBgSm from '@src/assets/pixel-office/sm.webp'; // 沙漠
import imgBgGt from '@src/assets/pixel-office/gt.webp'; // 宫廷
import imgBgTy from '@src/assets/pixel-office/ty.webp'; // 庭园
import imgSofa from '@src/assets/pixel-office/sofa-idle-v4.webp';
import imgStarIdle from '@src/assets/pixel-office/star-idle-v5.png';
import imgStarWorking from '@src/assets/pixel-office/star-working-spritesheet-grid.png';
import imgPlants from '@src/assets/pixel-office/plants-spritesheet.webp';
import imgPosters from '@src/assets/pixel-office/posters-spritesheet.webp';
import imgCoffeeMachine from '@src/assets/pixel-office/coffee-machine-v3-grid.webp';
import imgServerroom from '@src/assets/pixel-office/serverroom-spritesheet.webp';
import imgCats from '@src/assets/pixel-office/cats-spritesheet.webp';
import imgDesk from '@src/assets/pixel-office/desk-v3.webp';
import imgFlowers from '@src/assets/pixel-office/flowers-bloom-v2.webp';
import imgErrorBug from '@src/assets/pixel-office/error-bug-spritesheet-grid.png';
import imgSyncAnim from '@src/assets/pixel-office/sync-animation-v3-grid.png';
import imgChuang from '@src/assets/pixel-office/chuang.png';
import imgBgMario from '@src/assets/pixel-office/mario.png';

// ─── 场景尺寸（与 index.html Phaser config 一致） ─────────────────────────────
const SCENE_W = 1280;
const SCENE_H = 720;

// ─── 主题配置（对应 index.html themeMap）─────────────────────────────────────

type ThemeKey = 'forest' | 'modern' | 'snow' | 'desert' | 'palace' | 'garden';

export const THEMES: ReadonlyArray<{ key: ThemeKey; label: string; src: string; color: string }> = [
  { key: 'forest', label: '森林', src: imgOfficeBg, color: '#3A2E26' },
  { key: 'modern', label: '现代', src: imgBgXd, color: '#2F1F0F' },
  { key: 'snow',   label: '雪天', src: imgBgXt, color: '#11121E' },
  { key: 'desert', label: '沙漠', src: imgBgSm, color: '#1F170C' },
  { key: 'palace', label: '宫廷', src: imgBgGt, color: '#2E100A' },
  { key: 'garden', label: '庭院', src: imgBgTy, color: '#0A1405' },
];

/** 各主题激活态配色，来自 index.html .in-game-theme-btn[data-theme].active */
const THEME_ACTIVE: Record<ThemeKey, { border: string; bg: string; color: string }> = {
  forest: { border: '#49995B', bg: '#396048', color: '#fff' },
  modern: { border: '#F1AE7A', bg: '#C28454', color: '#fff' },
  snow:   { border: '#A8BCED', bg: '#637AB4', color: '#fff' },
  desert: { border: '#F5BA7A', bg: '#CB8D4A', color: '#FFF1E1' },
  palace: { border: '#D3776B', bg: '#A4463A', color: '#FFE3DF' },
  garden: { border: '#85B16E', bg: '#5C8845', color: '#E2FFD3' },
};

function srcToTheme(src: string): ThemeKey | null {
  return THEMES.find((t) => t.src === src)?.key ?? null;
}

export const THEME_STORAGE_KEY = 'pixel-office-theme';

function loadSavedTheme(): (typeof THEMES)[number] | null {
  try {
    const key = localStorage.getItem(THEME_STORAGE_KEY) as ThemeKey | null;
    return THEMES.find((t) => t.key === key) ?? null;
  } catch {
    return null;
  }
}

function saveTheme(key: ThemeKey): void {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, key);
  } catch {
    // ignore storage errors (e.g. private mode)
  }
}

// ─── 工具函数 ──────────────────────────────────────────────────────────────────

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

function calcFrameCount(img: HTMLImageElement, fw: number, fh: number): number {
  if (!fw || !fh) return 1;
  const cols = Math.max(1, Math.floor(img.naturalWidth / fw));
  const rows = Math.max(1, Math.floor(img.naturalHeight / fh));
  return cols * rows;
}

function getFrameXY(
  frame: number,
  frameW: number,
  frameH: number,
  imgW: number,
): { sx: number; sy: number } {
  const perRow = Math.max(1, Math.floor(imgW / frameW));
  return { sx: (frame % perRow) * frameW, sy: Math.floor(frame / perRow) * frameH };
}

// ─── 精灵定义（参照 index.html preload + create） ─────────────────────────────

interface SpriteConf {
  key: string;
  src: string;
  /** 帧宽：0 = 整图 */
  fw: number;
  /** 帧高：0 = 整图 */
  fh: number;
  /** 动画起始帧（含） */
  frameStart: number;
  /** 动画结束帧（含），-1 = 自动从图片尺寸推算 */
  frameEnd: number;
  /** fps，0 = 静态 */
  fps: number;
  /** 在场景坐标系中的 X（锚点） */
  x: number;
  /** 在场景坐标系中的 Y（锚点） */
  y: number;
  /** 绘制缩放 */
  scale: number;
  /**
   * 原点 X（0 = 左, 0.5 = 中, 1 = 右）
   * 影响锚点相对于绘制区域的位置，与 Phaser setOrigin 语义一致
   */
  originX: number;
  /** 原点 Y（0 = 上, 0.5 = 中, 1 = 下） */
  originY: number;
  depth: number;
  defaultVisible: boolean;
  /** 是否支持点击切换随机帧 */
  interactive?: boolean;
  /** staticFrame = true 时初始帧随机选取（植物/猫/海报/花盆） */
  randomFrame?: boolean;
}

/**
 * 精灵配置表，坐标与帧率完全对齐 index.html 的 preload()/create()：
 *   - star_idle   : (814,272) 25fps frames 0-95
 *   - star_working: (227,313) 25fps frames 0-95
 *   - sofa        : (800,274) 静态
 *   - serverroom  : (1021,142) 12fps
 *   - coffee_machine: (659,397) 16fps
 *   - error_bug   : (1007,221) 25fps，error 状态固定位置
 *   - sync_anim   : (1143,579) 25fps，frames 1-103
 *   - flower      : (260,350) origin(0,0) 无动画
 *   - poster      : (252,71)  scale 0.8  无动画
 */
const SPRITE_CONFS: SpriteConf[] = [
  // ── 背景 ──────────────────────────────────────────────────────────────────
  {
    key: 'office_bg',
    src: imgOfficeBg,
    fw: 0, fh: 0, frameStart: 0, frameEnd: 0, fps: 0,
    x: 640, y: 360, scale: 1, originX: 0.5, originY: 0.5, depth: 0, defaultVisible: true,
  },
  // ── 沙发（静态，v4 webp） ──────────────────────────────────────────────────
  {
    key: 'sofa',
    src: imgSofa,
    fw: 0, fh: 0, frameStart: 0, frameEnd: 0, fps: 0,
    x: 800, y: 274, scale: 1, originX: 0.5, originY: 0.5, depth: 10, defaultVisible: true,
  },
  // ── 服务器区（非 idle 时播放） ────────────────────────────────────────────
  {
    key: 'serverroom',
    src: imgServerroom,
    fw: 180, fh: 251, frameStart: 0, frameEnd: -1, fps: 12,
    x: 1021, y: 142, scale: 1, originX: 0.5, originY: 0.5, depth: 2, defaultVisible: true,
  },
  // ── 植物 ×3（可点击，随机帧） ─────────────────────────────────────────────
  {
    key: 'plant1',
    src: imgPlants,
    fw: 160, fh: 160, frameStart: 0, frameEnd: 15, fps: 0,
    x: 565, y: 178, scale: 1, originX: 0.5, originY: 0.5, depth: 5, defaultVisible: true,
    interactive: true, randomFrame: true,
  },
  // ── 床（右下角静态装饰，256×256，depth 低于植物在其后面） ─────────────────
  {
    key: 'chuang',
    src: imgChuang,
    fw: 0, fh: 0, frameStart: 0, frameEnd: 0, fps: 0,
    x: 1150, y: 590, scale: 1, originX: 0.5, originY: 0.5, depth: 3, defaultVisible: true,
  },
  {
    key: 'plant2',
    src: imgPlants,
    fw: 160, fh: 160, frameStart: 0, frameEnd: 15, fps: 0,
    x: 230, y: 185, scale: 1, originX: 0.5, originY: 0.5, depth: 5, defaultVisible: true,
    interactive: true, randomFrame: true,
  },
  {
    key: 'plant3',
    src: imgPlants,
    fw: 160, fh: 160, frameStart: 0, frameEnd: 15, fps: 0,
    x: 977, y: 496, scale: 1, originX: 0.5, originY: 0.5, depth: 5, defaultVisible: true,
    interactive: true, randomFrame: true,
  },
  // ── 海报（可点击，随机帧，scale 0.8） ─────────────────────────────────────
  {
    key: 'poster',
    src: imgPosters,
    fw: 160, fh: 160, frameStart: 0, frameEnd: -1, fps: 0,
    x: 252, y: 71, scale: 0.8, originX: 0.5, originY: 0.5, depth: 4, defaultVisible: true,
    interactive: true, randomFrame: true,
  },
  // ── 咖啡机（持续动画，16fps） ─────────────────────────────────────────────
  {
    key: 'coffee_machine',
    src: imgCoffeeMachine,
    fw: 230, fh: 230, frameStart: 0, frameEnd: -1, fps: 16,
    x: 659, y: 397, scale: 1, originX: 0.5, originY: 0.5, depth: 99, defaultVisible: true,
  },
  // ── 猫咪（可点击，随机帧，最顶层） ───────────────────────────────────────
  {
    key: 'cat',
    src: imgCats,
    fw: 160, fh: 160, frameStart: 0, frameEnd: -1, fps: 0,
    x: 94, y: 557, scale: 1, originX: 0.5, originY: 0.5, depth: 2000, defaultVisible: true,
    interactive: true, randomFrame: true,
  },
  // ── 同步动画（syncing 状态播放，frames 1-103，25fps） ────────────────────
  {
    key: 'sync_anim',
    src: imgSyncAnim,
    fw: 256, fh: 256, frameStart: 1, frameEnd: -1, fps: 25,
    x: 1143, y: 579, scale: 1, originX: 0.5, originY: 0.5, depth: 40, defaultVisible: false,
  },
  // ── 错误虫子（error 状态固定坐标播放，25fps） ────────────────────────────
  {
    key: 'error_bug',
    src: imgErrorBug,
    fw: 220, fh: 220, frameStart: 0, frameEnd: -1, fps: 25,
    x: 1007, y: 221, scale: 0.9, originX: 0.5, originY: 0.5, depth: 50, defaultVisible: false,
  },
  // ── Star 工作动画（writing/researching/executing，25fps） ─────────────────
  {
    key: 'star_working',
    src: imgStarWorking,
    fw: 300, fh: 300, frameStart: 0, frameEnd: -1, fps: 25,
    x: 227, y: 313, scale: 0.9, originX: 0.5, originY: 0.5, depth: 900, defaultVisible: false,
  },
  // ── 办公桌（前景遮罩） ─────────────────────────────────────────────────────
  {
    key: 'desk',
    src: imgDesk,
    fw: 0, fh: 0, frameStart: 0, frameEnd: 0, fps: 0,
    x: 218, y: 417, scale: 1, originX: 0.5, originY: 0.5, depth: 1001, defaultVisible: true,
  },
  // ── 花盆（origin 0,0 → top-left，可点击，随机帧） ─────────────────────────
  {
    key: 'flower',
    src: imgFlowers,
    fw: 96, fh: 96, frameStart: 0, frameEnd: 15, fps: 0,
    x: 260, y: 350, scale: 1, originX: 0, originY: 0, depth: 1100, defaultVisible: true,
    interactive: true, randomFrame: true,
  },
  // ── Star 待命动画（idle 状态在沙发旁，25fps） ─────────────────────────────
  {
    key: 'star_idle',
    src: imgStarIdle,
    fw: 256, fh: 256, frameStart: 0, frameEnd: -1, fps: 25,
    x: 814, y: 272, scale: 1, originX: 0.5, originY: 0.5, depth: 20, defaultVisible: true,
  },
];

// ─── 模块级图片缓存（preloadPixelOfficeAssets + OfficeScene 共享）─────────────

/** 所有需预加载的图片 URL（去重，含彩蛋背景） */
const ALL_ASSET_SRCS: readonly string[] = [
  ...new Set([
    ...SPRITE_CONFS.map((c) => c.src),
    ...THEMES.map((t) => t.src),
    imgBgMario,
  ]),
];

/** 模块级缓存：preload 后被 OfficeScene 直接复用，避免重复解码 */
export const GLOBAL_IMG_CACHE = new Map<string, HTMLImageElement>();

let _preloadPromise: Promise<void> | null = null;

/**
 * 预加载像素办公室全部图片资源。
 * - 幂等：多次调用复用同一 Promise。
 * - 调用后立即返回（不阻塞渲染），适合在页面进入时 fire-and-forget。
 */
export function preloadPixelOfficeAssets(): Promise<void> {
  if (_preloadPromise) return _preloadPromise;
  _preloadPromise = Promise.all(
    ALL_ASSET_SRCS.map(async (src) => {
      if (GLOBAL_IMG_CACHE.has(src)) return;
      try {
        const img = await loadImage(src);
        GLOBAL_IMG_CACHE.set(src, img);
      } catch {
        // 单张失败不阻断整体
      }
    }),
  ).then(() => undefined);
  return _preloadPromise;
}

// ─── 场景内精灵运行时状态 ──────────────────────────────────────────────────────

interface SpriteState {
  conf: SpriteConf;
  img: HTMLImageElement;
  currentFrame: number;
  totalFrames: number;
  lastFrameTime: number;
  visible: boolean;
  scale: number;
}

// ─── 气泡描述 ──────────────────────────────────────────────────────────────────

interface BubbleState {
  text: string;
  /** 在 canvas 像素坐标系中的锚点（气泡底部中心） */
  canvasX: number;
  canvasY: number;
  until: number;
  style: 'status' | 'cat';
}

// ─── 场景类（对应 index.html 的 Phaser Scene） ────────────────────────────────

class OfficeScene {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private sprites = new Map<string, SpriteState>();
  private statusBubble: BubbleState | null = null;
  private catBubble: BubbleState | null = null;
  private status: OfficeStatus = 'idle';
  private rafId = 0;
  private lastBubbleAt = 0;
  private lastCatBubbleAt = 0;
  /** 实例级缓存（从 GLOBAL_IMG_CACHE 同步而来，避免重复解码） */
  private imageCache = new Map<string, HTMLImageElement>();

  /** 用于 serverroom 的帧 0 静止兜底 */
  private serverroomAnimating = false;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d')!;
  }

  // ── preload：优先命中全局缓存，缺失部分并行加载 ────────────────────────

  async preload(): Promise<void> {
    // 将全局缓存同步到实例缓存；未命中的并行补加载
    const missing: string[] = [];
    for (const src of ALL_ASSET_SRCS) {
      const cached = GLOBAL_IMG_CACHE.get(src);
      if (cached) {
        this.imageCache.set(src, cached);
      } else {
        missing.push(src);
      }
    }
    if (missing.length > 0) {
      await Promise.all(
        missing.map(async (src) => {
          try {
            const img = await loadImage(src);
            GLOBAL_IMG_CACHE.set(src, img);
            this.imageCache.set(src, img);
          } catch {
            // 单张失败不阻断整体
          }
        }),
      );
    }

    for (const conf of SPRITE_CONFS) {
      const img = this.imageCache.get(conf.src);
      if (!img) continue;
      const total =
        conf.fw && conf.fh ? calcFrameCount(img, conf.fw, conf.fh) : 1;
      const end = conf.frameEnd === -1 ? total - 1 : Math.min(conf.frameEnd, total - 1);
      const frameCount = Math.max(1, end - conf.frameStart + 1);
      const initFrame = conf.randomFrame
        ? conf.frameStart + Math.floor(Math.random() * frameCount)
        : conf.frameStart;

      this.sprites.set(conf.key, {
        conf,
        img,
        currentFrame: initFrame,
        totalFrames: frameCount,
        lastFrameTime: 0,
        visible: conf.defaultVisible,
        scale: conf.scale,
      });
    }
  }

  // ── create：对应 index.html create()，应用初始状态 ───────────────────────

  create(): void {
    this.applyVisualState('idle');
    this.startLoop();
  }

  private startLoop(): void {
    const loop = (now: number) => {
      this.update(now);
      this.render(now);
      this.rafId = requestAnimationFrame(loop);
    };
    this.rafId = requestAnimationFrame(loop);
  }

  destroy(): void {
    cancelAnimationFrame(this.rafId);
  }

  // ── update：对应 index.html update(time) ─────────────────────────────────

  private update(now: number): void {
    const BUBBLE_INTERVAL = 8_000;
    const CAT_BUBBLE_INTERVAL = 18_000;

    if (now - this.lastBubbleAt > BUBBLE_INTERVAL) {
      this.showStatusBubble(now);
      this.lastBubbleAt = now;
    }
    if (now - this.lastCatBubbleAt > CAT_BUBBLE_INTERVAL) {
      this.showCatBubble(now);
      this.lastCatBubbleAt = now;
    }

    // serverroom 帧兜底（idle → frame0）
    const sr = this.sprites.get('serverroom');
    if (sr) {
      if (!this.serverroomAnimating && sr.currentFrame !== 0) {
        sr.currentFrame = 0;
      }
    }
  }

  // ── render：主渲染循环（对应 Phaser 的 render + update） ─────────────────

  private render(now: number): void {
    const { canvas, ctx } = this;
    const scale = canvas.width / SCENE_W;
    const height = (canvas.width / SCENE_W) * SCENE_H;

    ctx.clearRect(0, 0, canvas.width, height);
    ctx.save();
    ctx.scale(scale, scale);

    const sorted = Array.from(this.sprites.values()).sort(
      (a, b) => a.conf.depth - b.conf.depth,
    );

    for (const state of sorted) {
      if (!state.visible) continue;
      this.drawSprite(state, now);
    }

    ctx.restore();

    // 气泡绘制在 canvas 坐标系（不受 scale 影响）
    this.drawBubble(ctx, this.statusBubble, now);
    this.drawBubble(ctx, this.catBubble, now);
  }

  private drawSprite(state: SpriteState, now: number): void {
    const { ctx } = this;
    const { conf, img } = state;
    const sc = state.scale;
    const { x, y, originX, originY, fw, fh } = conf;

    if (fw && fh) {
      // 精灵表动画帧推进
      if (conf.fps > 0) {
        const interval = 1000 / conf.fps;
        if (now - state.lastFrameTime >= interval) {
          state.currentFrame =
            conf.frameStart +
            ((state.currentFrame - conf.frameStart + 1) % state.totalFrames);
          state.lastFrameTime = now;
        }
      }
      const { sx, sy } = getFrameXY(state.currentFrame, fw, fh, img.naturalWidth);
      const dw = fw * sc;
      const dh = fh * sc;
      ctx.drawImage(img, sx, sy, fw, fh, x - dw * originX, y - dh * originY, dw, dh);
    } else {
      const dw = img.naturalWidth * sc;
      const dh = img.naturalHeight * sc;
      ctx.drawImage(img, x - dw * originX, y - dh * originY, dw, dh);
    }
  }

  /** 设备像素比（Retina 屏为 2，普通屏为 1） */
  private get dpr(): number {
    return window.devicePixelRatio || 1;
  }

  private drawBubble(
    ctx: CanvasRenderingContext2D,
    bubble: BubbleState | null,
    now: number,
  ): void {
    if (!bubble) return;
    if (bubble.until <= now) {
      if (bubble === this.statusBubble) this.statusBubble = null;
      if (bubble === this.catBubble) this.catBubble = null;
      return;
    }

    // 气泡绘制在物理像素坐标系，尺寸须乘以 dpr 以在 Retina 屏保持清晰
    const dpr = this.dpr;

    // 与 index.html showBubble / showCatBubble 保持一致的配色
    const fontSize = Math.round(13 * dpr);
    ctx.font = `${fontSize}px "PingFang SC", "Microsoft YaHei", sans-serif`;

    const textW = ctx.measureText(bubble.text).width;
    const padX = Math.round(10 * dpr);
    const boxH = Math.round(30 * dpr);
    const boxW = textW + padX * 2;
    const radius = Math.round(6 * dpr);
    const bx = bubble.canvasX;
    const by = bubble.canvasY - boxH;

    ctx.save();
    ctx.fillStyle = 'rgba(226,197,150,0.95)'; // #E2C596 at 0.95
    ctx.strokeStyle = '#996547';
    ctx.lineWidth = Math.round(2 * dpr);

    if (ctx.roundRect) {
      ctx.beginPath();
      ctx.roundRect(bx - boxW / 2, by, boxW, boxH, radius);
      ctx.fill();
      ctx.stroke();
    } else {
      ctx.fillRect(bx - boxW / 2, by, boxW, boxH);
      ctx.strokeRect(bx - boxW / 2, by, boxW, boxH);
    }

    ctx.fillStyle = '#6C3F25';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(bubble.text, bx, by + boxH / 2);
    ctx.restore();
  }

  // ── applyVisualState：对应 index.html applyVisualState(nextState) ─────────

  applyVisualState(status: OfficeStatus): void {
    this.status = status;
    const set = (key: string, visible: boolean) => {
      const s = this.sprites.get(key);
      if (s) s.visible = visible;
    };
    const reset = (key: string) => {
      const s = this.sprites.get(key);
      if (s) { s.visible = false; s.currentFrame = s.conf.frameStart; }
    };

    // idle: Star idle 在沙发边，working/syncing/error 隐藏
    if (status === 'idle') {
      set('star_idle', true);
      reset('star_working');
      reset('sync_anim');
      reset('error_bug');
      this.serverroomAnimating = false;
    } else if (status === 'error') {
      reset('star_idle');
      reset('star_working');
      reset('sync_anim');
      set('error_bug', true);
      this.serverroomAnimating = false;
    } else if (status === 'syncing') {
      reset('star_idle');
      reset('star_working');
      reset('error_bug');
      const sa = this.sprites.get('sync_anim');
      if (sa) { sa.visible = true; sa.currentFrame = sa.conf.frameStart; }
      this.serverroomAnimating = true;
    } else {
      // writing / researching / executing
      reset('star_idle');
      set('star_working', true);
      reset('sync_anim');
      reset('error_bug');
      this.serverroomAnimating = true;
    }
  }

  // ── 气泡显示（对应 index.html showBubble / showCatBubble） ───────────────

  private sceneToCanvas(sceneX: number, sceneY: number): { cx: number; cy: number } {
    const scale = this.canvas.width / SCENE_W;
    return { cx: sceneX * scale, cy: sceneY * scale };
  }

  private showStatusBubble(now: number): void {
    const texts = BUBBLE_TEXTS[this.status];
    if (!texts?.length) return;

    let anchorSceneX = 814, anchorSceneY = 200;
    if (this.status === 'syncing') {
      anchorSceneX = 1143; anchorSceneY = 490;
    } else if (this.status === 'error') {
      anchorSceneX = 1007; anchorSceneY = 140;
    } else if (this.status !== 'idle') {
      anchorSceneX = 227; anchorSceneY = 220;
    }

    const { cx, cy } = this.sceneToCanvas(anchorSceneX, anchorSceneY);
    const text = texts[Math.floor(Math.random() * texts.length)];
    this.statusBubble = { text, canvasX: cx, canvasY: cy, until: now + 3000, style: 'status' };
  }

  private showCatBubble(now: number): void {
    const { cx, cy } = this.sceneToCanvas(94, 477); // cat y - 80
    const text = CAT_BUBBLE_TEXTS[Math.floor(Math.random() * CAT_BUBBLE_TEXTS.length)];
    this.catBubble = { text, canvasX: cx, canvasY: cy, until: now + 4000, style: 'cat' };
  }

  // ── 对外接口 ─────────────────────────────────────────────────────────────

  setStatus(status: OfficeStatus): void {
    if (this.status === status) return;
    this.applyVisualState(status);
  }

  setBgSrc(src: string): void {
    const state = this.sprites.get('office_bg');
    if (!state) return;
    // 优先命中实例缓存 → 全局缓存（页面预加载后无需网络请求）
    const cached = this.imageCache.get(src) ?? GLOBAL_IMG_CACHE.get(src);
    if (cached) {
      state.img = cached;
      return;
    }
    loadImage(src).then((img) => {
      GLOBAL_IMG_CACHE.set(src, img);
      this.imageCache.set(src, img);
      state.img = img;
    });
  }

  /** 处理 canvas 点击事件（像素坐标 → 场景坐标 → 命中测试） */
  handleClick(canvasX: number, canvasY: number, now: number, onBgClick?: () => void): void {
    const scale = this.canvas.width / SCENE_W;
    const sceneX = canvasX / scale;
    const sceneY = canvasY / scale;

    for (const [key, state] of this.sprites) {
      if (!state.conf.interactive || !state.visible) continue;
      if (!this.hitTest(sceneX, sceneY, state)) continue;

      // 切换随机帧
      if (state.totalFrames > 1) {
        state.currentFrame =
          state.conf.frameStart + Math.floor(Math.random() * state.totalFrames);
      }

      // 猫咪点击 → 立即弹出气泡
      if (key === 'cat') {
        const { cx, cy } = this.sceneToCanvas(state.conf.x, state.conf.y - 80);
        const text = CAT_BUBBLE_TEXTS[Math.floor(Math.random() * CAT_BUBBLE_TEXTS.length)];
        this.catBubble = { text, canvasX: cx, canvasY: cy, until: now + 3500, style: 'cat' };
        this.lastCatBubbleAt = now;
      }
      return;
    }

    // 未命中任何交互精灵 → 通知父组件（可用于换背景）
    onBgClick?.();
  }

  private hitTest(sceneX: number, sceneY: number, state: SpriteState): boolean {
    const { conf, img, scale } = state;
    const dw = (conf.fw || img.naturalWidth) * scale;
    const dh = (conf.fh || img.naturalHeight) * scale;
    const left = conf.x - dw * conf.originX;
    const top = conf.y - dh * conf.originY;
    return sceneX >= left && sceneX <= left + dw && sceneY >= top && sceneY <= top + dh;
  }
}

// ─── React 组件 ────────────────────────────────────────────────────────────────

interface Props {
  status: OfficeStatus;
  width: number;
  bgSrc?: string;
  /** 办公室名称，显示在底部名牌中 */
  officeName?: string;
  /** 点击背景非交互区域时触发（可用于随机换背景） */
  onBgClick?: () => void;
  /** 主题切换时回调，参数为当前主题的 color 色值 */
  onThemeChange?: (color: string) => void;
}

const PixelOfficeCanvas: React.FC<Props> = ({
  status,
  width,
  bgSrc,
  officeName,
  onBgClick,
  onThemeChange,
}) => {
  const height = Math.round((width / SCENE_W) * SCENE_H);
  // 物理像素尺寸 = CSS 尺寸 × devicePixelRatio，避免 Retina 屏模糊
  const dpr = typeof window !== 'undefined' ? (window.devicePixelRatio || 1) : 1;
  const physicalW = Math.round(width * dpr);
  const physicalH = Math.round(height * dpr);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sceneRef = useRef<OfficeScene | null>(null);
  const readyRef = useRef(false);

  /** 当前激活的主题（null = 不是内置主题，如随机背景） */
  const [activeTheme, setActiveTheme] = useState<ThemeKey | null>(
    () => loadSavedTheme()?.key ?? 'forest',
  );

  /** 调试面板显示状态（暗门：名牌连点 5 次触发） */
  const [showDebug, setShowDebug] = useState(false);
  /** 调试面板中当前选中的状态（与场景解耦，不影响 props.status） */
  const [debugStatus, setDebugStatus] = useState<OfficeStatus>(status);
  const plaqueClickCountRef = useRef(0);
  const plaqueClickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /** 彩蛋：连点背景 5 次切换 mario 背景 */
  const bgEggClickCountRef = useRef(0);
  const bgEggClickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── 初始化场景（仅一次） ────────────────────────────────────────────────────
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const scene = new OfficeScene(canvas);
    sceneRef.current = scene;

    scene.preload().then(() => {
      readyRef.current = true;
      scene.create();
      scene.setStatus(status);
      // 本地存储的主题优先；无存档时再用父组件传入的 bgSrc
      const saved = loadSavedTheme();
      if (saved) {
        scene.setBgSrc(saved.src);
        onThemeChange?.(saved.color);
      }
    });

    return () => {
      scene.destroy();
      sceneRef.current = null;
      readyRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // status 变化 → 更新场景状态
  useEffect(() => {
    if (readyRef.current) sceneRef.current?.setStatus(status);
  }, [status]);

  // bgSrc（来自父组件）变化 → 替换背景，同步激活主题高亮
  useEffect(() => {
    if (!readyRef.current || !bgSrc) return;
    sceneRef.current?.setBgSrc(bgSrc);
    setActiveTheme(srcToTheme(bgSrc));
  }, [bgSrc]);

  // ── 主题按钮点击（选中后持久化到 localStorage） ──────────────────────────
  const handleThemeClick = useCallback(
    (theme: (typeof THEMES)[number]) => {
      setActiveTheme(theme.key);
      sceneRef.current?.setBgSrc(theme.src);
      saveTheme(theme.key);
      onThemeChange?.(theme.color);
    },
    [onThemeChange],
  );

  // ── 名牌暗门：连点 5 次切换调试面板 ─────────────────────────────────────
  const handlePlaqueClick = useCallback(() => {
    plaqueClickCountRef.current += 1;
    if (plaqueClickTimerRef.current) clearTimeout(plaqueClickTimerRef.current);
    plaqueClickTimerRef.current = setTimeout(() => {
      plaqueClickCountRef.current = 0;
    }, 2000);
    if (plaqueClickCountRef.current >= 5) {
      plaqueClickCountRef.current = 0;
      setShowDebug((v) => !v);
    }
  }, []);

  // ── 调试状态按钮 ─────────────────────────────────────────────────────────
  const handleDebugStatus = useCallback((s: OfficeStatus) => {
    setDebugStatus(s);
    sceneRef.current?.setStatus(s);
  }, []);

  // ── 背景彩蛋：连点 5 次切换 mario 背景 ──────────────────────────────────
  const handleBgClickWithEgg = useCallback(() => {
    onBgClick?.();

    bgEggClickCountRef.current += 1;
    if (bgEggClickTimerRef.current) clearTimeout(bgEggClickTimerRef.current);
    bgEggClickTimerRef.current = setTimeout(() => {
      bgEggClickCountRef.current = 0;
    }, 2000);

    if (bgEggClickCountRef.current >= 5) {
      bgEggClickCountRef.current = 0;
      sceneRef.current?.setBgSrc(imgBgMario);
      setActiveTheme(null);
    }
  }, [onBgClick]);

  // ── Canvas 点击 ──────────────────────────────────────────────────────────
  const handleCanvasClick = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const ratio = canvas.width / rect.width; // CSS px → 物理 px
      sceneRef.current?.handleClick(
        (e.clientX - rect.left) * ratio,
        (e.clientY - rect.top) * ratio,
        performance.now(),
        handleBgClickWithEgg,
      );
    },
    [handleBgClickWithEgg],
  );

  // ── 比例系数（所有叠加层尺寸随 canvas 宽度等比缩放） ──────────────────────
  const sc = width / SCENE_W;

  // 名牌尺寸
  const plaqueH = Math.max(22, Math.round(41 * sc));
  const plaqueFont = Math.max(10, Math.round(18 * sc));
  const plaqueStarFont = Math.max(11, Math.round(20 * sc));
  const plaquePadH = Math.max(6, Math.round(14 * sc));
  const plaqueGap = Math.max(4, Math.round(8 * sc));
  const plaqueBottom = Math.max(10, Math.round(27 * sc));
  const plaqueBorderR = Math.max(4, Math.round(8 * sc));

  // 主题按钮尺寸
  const btnW = Math.max(40, Math.round(59 * sc));
  const btnH = Math.max(22, Math.round(32 * sc));
  const btnFont = Math.max(10, Math.round(15 * sc));
  const btnGap = Math.max(3, Math.round(6 * sc));
  const panelBottom = Math.max(10, Math.round(27 * sc));
  const panelRight = Math.max(10, Math.round(27 * sc));
  const panelRowGap = Math.max(3, Math.round(6 * sc));
  const btnRadius = Math.max(2, Math.round(3 * sc));

  return (
    <div className="poc-wrap" style={{ position: 'relative', display: 'block', lineHeight: 0, width, height }}>
      <canvas
        ref={canvasRef}
        width={physicalW}
        height={physicalH}
        style={{ display: 'block', width, height, imageRendering: 'auto' }}
        onClick={handleCanvasClick}
      />

      {/* ── 办公室名牌（底部居中，连点 5 次唤出调试面板）──────────────────── */}
      {officeName && (
        <div
          className="poc-plaque"
          onClick={handlePlaqueClick}
          style={{
            position: 'absolute',
            top: plaqueBottom,
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: plaqueGap,
            padding: `0 ${plaquePadH}px`,
            height: plaqueH,
            background: '#E2C596',
            border: '2px solid #F0DFB5',
            borderRadius: plaqueBorderR,
            boxShadow: '0 0 0 2px #996547',
            zIndex: 100,
            cursor: 'default',
            userSelect: 'none',
            whiteSpace: 'nowrap',
          }}
        >
          <span style={{ fontSize: plaqueStarFont, lineHeight: 1, filter: 'drop-shadow(0 0 2px rgba(0,0,0,.3))' }}>⭐</span>
          <span
            style={{
              fontFamily: '"PingFang SC","Microsoft YaHei",sans-serif',
              fontSize: plaqueFont,
              fontWeight: 500,
              color: '#8A5130',
              WebkitFontSmoothing: 'antialiased',
            }}
          >
            {officeName}的小屋
          </span>
          <span style={{ fontSize: plaqueStarFont, lineHeight: 1, filter: 'drop-shadow(0 0 2px rgba(0,0,0,.3))' }}>⭐</span>
        </div>
      )}

      {/* ── 调试面板（暗门，连点名牌 5 次显示）────────────────────────────── */}
      {showDebug && (() => {
        const debugBtnW = Math.max(52, Math.round(72 * sc));
        const debugBtnH = Math.max(28, Math.round(38 * sc));
        const debugFont = Math.max(11, Math.round(15 * sc));
        const debugTitleFont = Math.max(12, Math.round(14 * sc));
        const debugPad = Math.max(8, Math.round(12 * sc));
        const debugGap = Math.max(6, Math.round(8 * sc));
        const DEBUG_STATUSES: Array<{ key: OfficeStatus; label: string }> = [
          { key: 'idle',    label: '待命' },
          { key: 'writing', label: '工作' },
          { key: 'syncing', label: '同步' },
          { key: 'error',   label: '报警' },
        ];
        return (
          <div
            style={{
              position: 'absolute',
              bottom: Math.max(10, Math.round(27 * sc)) + Math.max(22, Math.round(41 * sc)) + Math.max(6, Math.round(8 * sc)),
              left: '50%',
              transform: 'translateX(-50%)',
              background: '#0f1320',
              border: '2px solid #0e1119',
              borderRadius: 6,
              padding: `${debugPad}px ${Math.round(debugPad * 1.5)}px`,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: debugGap,
              zIndex: 200,
              boxShadow: '0 4px 16px rgba(0,0,0,0.6)',
              pointerEvents: 'auto',
            }}
          >
            <span style={{
              color: '#ffd700',
              fontFamily: '"PingFang SC","Microsoft YaHei",sans-serif',
              fontSize: debugTitleFont,
              fontWeight: 700,
              letterSpacing: 2,
              whiteSpace: 'nowrap',
            }}>
              Star 状态
            </span>
            <div style={{ display: 'flex', gap: debugGap }}>
              {DEBUG_STATUSES.map(({ key, label }) => {
                const isActive = debugStatus === key;
                return (
                  <button
                    key={key}
                    onClick={() => handleDebugStatus(key)}
                    style={{
                      width: debugBtnW,
                      height: debugBtnH,
                      fontSize: debugFont,
                      fontFamily: '"PingFang SC","Microsoft YaHei",sans-serif',
                      fontWeight: 500,
                      cursor: 'pointer',
                      borderRadius: 4,
                      border: isActive ? '2px solid #ffd700' : '2px solid #444',
                      background: isActive ? '#2a2040' : 'linear-gradient(180deg,#3a3a3a 0%,#282828 100%)',
                      color: isActive ? '#ffd700' : '#ccc',
                      boxShadow: isActive
                        ? '0 0 6px rgba(255,215,0,0.4)'
                        : 'inset 0 1px 0 rgba(255,255,255,0.12), 0 2px 4px rgba(0,0,0,0.4)',
                      transition: 'all 0.12s',
                      padding: 0,
                    }}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* ── 主题切换面板（底部右侧，两行 3 个按钮）─────────────────────────── */}
      <div
        className="poc-theme-panel"
        style={{
          position: 'absolute',
          bottom: panelBottom,
          right: panelRight,
          zIndex: 100,
          display: 'flex',
          flexDirection: 'column',
          gap: panelRowGap,
          pointerEvents: 'none',
        }}
      >
        {([THEMES.slice(0, 3), THEMES.slice(3)] as const).map((row, ri) => (
          <div
            key={ri}
            style={{
              display: 'flex',
              gap: btnGap,
              justifyContent: 'flex-end',
              pointerEvents: 'auto',
            }}
          >
            {row.map((theme) => {
              const isActive = activeTheme === theme.key;
              const ac = isActive ? THEME_ACTIVE[theme.key] : null;
              return (
                <button
                  key={theme.key}
                  onClick={() => handleThemeClick(theme)}
                  style={{
                    width: btnW,
                    height: btnH,
                    borderRadius: btnRadius,
                    border: `1.5px solid ${isActive ? ac!.border : '#F0DFB5'}`,
                    background: isActive ? ac!.bg : '#E2C596',
                    boxShadow: isActive ? 'none' : '0 0 0 1.6px #996547',
                    color: isActive ? ac!.color : '#8A5130',
                    fontSize: btnFont,
                    fontFamily: '"PingFang SC","Microsoft YaHei",sans-serif',
                    fontWeight: 500,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 0,
                    lineHeight: 'normal',
                    transition: 'all 0.15s',
                  }}
                >
                  {theme.label}
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

export default PixelOfficeCanvas;
